import type { ChangeEvent } from 'react';
import { forwardRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import {
  AddAttachments,
  AddMessageLayout,
  MESSAGE_STATUS,
  PersonalContactDetailsWarningModal,
  SSN_WARNING_DATA,
  TextareaAutoSize,
  TooltipWrapper,
  UploadedAttachmentList,
  useMessageDrafts,
  useSendOnEnter,
} from '@zg-rentals/satellite-components';
import type { PersonalContactDetailsWarningData } from '@zg-rentals/satellite-components';
import { getSendWarning } from './getSendWarning';
import type { SendWarningType } from './getSendWarning';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import { bootstrap } from '../../bootstrap';
import ConfigConstants from '../../constants/ConfigConstants';
import StringConstants from '../../constants/StringConstants';
import { logAppError } from '../../utils/errorUtils';
import {
  emitSendMessageEvent,
  emitSendMessageSmsAsSourceEvent,
  emitSendMessageWithAttachmentsEvent,
  SEND_MESSAGE_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/sendMessage';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { AppContext } from '../../contexts/AppContextProvider';
import type { ConversationMessageAttachment } from '../../types/conversationMessages';
import type { DecoratedMessage } from '../MessageList';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteWebRenterConversationHandler_getLatestConversations_Path,
} from '@zg-rentals/ts-satellite-client';
import type { AttachmentsUploadedForAddMessage } from '@zg-rentals/satellite-components';
import type { User } from '../../AuthProvider';
import type { AttachmentsForMessage } from '../MessageAttachments';
import ImagesViewer from '../ImagesViewer';
import {
  emitOpenPersonalInfoWarningModalEvent,
  emitSendAnywayInPersonalInfoWarningModalEvent,
} from '../../constants/clickstreamEvents/personalInfoWarningModal';
import {
  useSatelliteWebRenterConversationHandler_addMessage,
  useSatelliteWebRenterConversationHandler_getMessages,
} from '@zg-rentals/ts-satellite-client-react-query';

const AttachmentsUploader = dynamic(() => import('../AttachmentsUploader'), {
  loading: () => null,
  ssr: false,
});

const logger = bootstrap.logger.child({ name: 'AddMessage' });

type Props = {
  user: User;
  handleAddMessage(optimisticMessage: DecoratedMessage): void;
  isConversationBlockedByZillow: boolean;
  shouldAllowSendingAttachmentsOnly: boolean;
  shouldOpenAttachmentsUploader: boolean;
  shouldFetchOlderConversations: boolean;
  updateTemporaryMessage(messageKey: string, updates: Partial<DecoratedMessage>): void;
};

export type SetAttachmentsUploadedForAddMessage = (uploadedAttachments: AttachmentsUploadedForAddMessage) => void;

const AddMessage = forwardRef<HTMLDivElement, Props>(
  (
    {
      user,
      handleAddMessage,
      isConversationBlockedByZillow,
      shouldAllowSendingAttachmentsOnly,
      shouldOpenAttachmentsUploader,
      shouldFetchOlderConversations,
      updateTemporaryMessage,
    }: Props,
    ref,
  ) => {
    const queryClient = useQueryClient();
    const { isImpersonator } = useContext(AppContext);
    const { conversationId, folder, messageJustSentRef } = useContext(InboxPageContext);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    // Determine if we should track responses for users redirected to web from an SMS link click
    const {
      query: { utm_content, utm_source },
    } = useRouter();
    const isComingFromSms = utm_source === 'sms';
    const conversationIdParamForSMSTracking = isComingFromSms ? (utm_content?.toString().split('.')[0] ?? '') : '';

    const [shouldTrackResponseFromSMSLink, setShouldTrackResponseFromSMSLink] = useState(
      conversationIdParamForSMSTracking === conversationId,
    );
    const [messageInputText, setMessageInputText] = useState('');
    const [tempMessageKey, setTempMessageKey] = useState(Math.random().toString());
    const [isTextAreaFocused, setIsTextAreaFocused] = useState(true);

    const [uploadedAttachments, setUploadedAttachments] = useState<AttachmentsUploadedForAddMessage>([]);
    const [isPersonalContactWarningModalOpen, setIsPersonalContactWarningModalOpen] = useState(false);
    const [activeWarning, setActiveWarning] = useState<SendWarningType | null>(null);
    const [imageAttachments, setImageAttachments] = useState<AttachmentsForMessage>([]);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [shouldShowImagesViewer, setShouldShowImagesViewer] = useState(false);

    const { clearMessageDrafts } = useMessageDrafts<AttachmentsUploadedForAddMessage[0]>({
      conversationId: conversationId || '',
      logger,
      messageInputText,
      setMessageInputText,
      uploadedAttachments,
      setUploadedAttachments,
    });

    const { personalContactWarningData } = useUserWarnings();

    let timeoutId: null | ReturnType<typeof setTimeout> = null;
    const hasNoMessageText = messageInputText.trim().length === 0;
    const hasNoMessageTextAndNoAttachments = hasNoMessageText && uploadedAttachments.length === 0;
    const isMessageInputEmpty = shouldAllowSendingAttachmentsOnly ? hasNoMessageTextAndNoAttachments : hasNoMessageText;
    const isSpamFolder = folder === SatelliteConversationFolderTypeV2.SPAM;
    const isAddMessageDisabled = isImpersonator || isConversationBlockedByZillow || isSpamFolder;
    const isSendButtonDisabled = isImpersonator || isMessageInputEmpty || isAddMessageDisabled;
    const [shouldFocusAddMessage, setShouldFocusAddMessage] = useState(isTextAreaFocused && !isAddMessageDisabled);
    const [shouldShowAttachmentsUploader, setShouldShowAttachmentsUploader] = useState(false);

    const { mutateAsync: addMessage } = useSatelliteWebRenterConversationHandler_addMessage();

    useEffect(() => {
      /**
       * Revoke generated preview urls from AttachmentsUploader to
       * prevent memory leaks, will run on unmount
       * https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL
       */
      return () =>
        uploadedAttachments.forEach((uploadedAttachment) => {
          if (uploadedAttachment.generatedPreviewUrl) {
            URL.revokeObjectURL(uploadedAttachment.generatedPreviewUrl);
          }
        });
    }, []);

    // Focus input when attachments are added
    useEffect(() => {
      handleFocus();
    }, [uploadedAttachments]);

    useEffect(() => {
      if (shouldOpenAttachmentsUploader) {
        setShouldShowAttachmentsUploader(shouldOpenAttachmentsUploader);
      }
    }, [shouldOpenAttachmentsUploader]);

    useLayoutEffect(() => {
      if (isTextAreaFocused && textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }, [isTextAreaFocused]);

    const tooltipText = useMemo(() => {
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED;
      }
      if (isSpamFolder) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      return '';
    }, [isConversationBlockedByZillow, isImpersonator, isSpamFolder]);

    const handleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      const messageText = event.target.value;
      setMessageInputText(messageText);
    };

    const { onKeyDown: handleKeyDown, placeholder } = useSendOnEnter({
      onSend: () => sendMessage(),
      canSend: !isSendButtonDisabled,
    });

    const handleOptimisticMessage = (
      messageToSend: string,
      attachmentsToSend: Array<ConversationMessageAttachment>,
    ) => {
      // Create a dummy message for optimistic update
      const optimisticMessage: DecoratedMessage = {
        aiAutoResponseType: undefined,
        attachments: attachmentsToSend || [],
        message: messageToSend.trim(),
        isMessageOwner: true,
        messageDateMs: String(Date.now()),
        messageId: tempMessageKey,
        messageType: 'listingMessage',
        senderName: '',
        sendStatus: MESSAGE_STATUS.SENDING,
        temporaryMessageKey: tempMessageKey,
      };

      return handleAddMessage(optimisticMessage);
    };

    const addMessageToConversation = async (
      messageToSend: string,
      attachmentsZDocIds: Array<string>,
    ): Promise<void | null> => {
      try {
        if (!conversationId) return null;

        const result = await addMessage({
          postData: {
            conversationId,
            message: messageToSend,
            uploadedZdocIds: attachmentsZDocIds,
          },
        });

        if (result) {
          const { messageId, messageDateMs } = result;

          updateTemporaryMessage(tempMessageKey, {
            messageId,
            sendStatus: MESSAGE_STATUS.SENT,
            messageDateMs: String(messageDateMs),
          });

          clearMessageDrafts();
          setTempMessageKey(Math.random().toString());

          if (timeoutId) clearTimeout(timeoutId);

          emitSendMessageEvent(user);

          if (attachmentsZDocIds.length > 0) {
            emitSendMessageWithAttachmentsEvent(user);
          }

          // Only track responses from SMS links once
          if (shouldTrackResponseFromSMSLink) {
            setShouldTrackResponseFromSMSLink(false);
            emitSendMessageSmsAsSourceEvent(user);
          }

          // Mark that we just sent so MessageList WebSocket handler and ConversationList effect skip
          // refetching getMessages, avoiding duplicate API calls. Single refetch here (conversationId only).
          if (conversationId && messageJustSentRef) {
            messageJustSentRef.current = { conversationId, messageId, at: Date.now() };
            setTimeout(() => {
              if (messageJustSentRef.current?.messageId === messageId) {
                messageJustSentRef.current = null;
              }
            }, ConfigConstants.MESSAGE_JUST_SENT_DEBOUNCE_MS);
          }
          queryClient.invalidateQueries({
            queryKey: useSatelliteWebRenterConversationHandler_getMessages.getKey({ conversationId }),
            exact: true,
          });
          queryClient.invalidateQueries({
            queryKey: [
              SatelliteWebRenterConversationHandler_getLatestConversations_Path,
              {
                folder,
                daysBack: shouldFetchOlderConversations ? ConfigConstants.MAX_DAYS_BACK_FOR_CONVERSATIONS : undefined,
              },
            ],
          });
        }
      } catch (error) {
        logAppError({
          context: { errorLocation: 'component.AddMessage.addMessageToConversation' },
          message: error.message,
          error,
        });

        updateTemporaryMessage(tempMessageKey, { sendStatus: MESSAGE_STATUS.FAILED });
      }
    };

    const sendMessage = (bypassWarning = false) => {
      if (isMessageInputEmpty) {
        return;
      }

      // Check for PII (email/phone/SSN) and show the warning modal with the
      // appropriate copy unless we're bypassing the warning.
      if (!bypassWarning) {
        const warningType = getSendWarning(messageInputText);
        if (warningType) {
          setActiveWarning(warningType);
          setIsPersonalContactWarningModalOpen(true);
          emitOpenPersonalInfoWarningModalEvent();
          return;
        }
      }

      const messageToSend = messageInputText.trim();
      const { attachmentsForOptimisticMessage = [], zDocIds = [] } = uploadedAttachments.length
        ? getAttachmentDataForAddMessageV2Mutation(uploadedAttachments)
        : {};

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setMessageInputText('');
      setUploadedAttachments([]);
      handleOptimisticMessage(messageToSend, attachmentsForOptimisticMessage);

      timeoutId = setTimeout(() => {
        addMessageToConversation(messageToSend, zDocIds);
      }, 1500);
    };

    const handleFocus = () => {
      setIsTextAreaFocused(true);
      setShouldFocusAddMessage(true);
    };

    const handleBlur = () => {
      setIsTextAreaFocused(false);
      setShouldFocusAddMessage(false);
    };

    const handleRemoveUploadedAttachment = useCallback(
      (zDocId: string) => {
        const filteredUploads = uploadedAttachments.filter((attachment) => attachment.zDocId !== zDocId);
        setUploadedAttachments(filteredUploads);
      },
      [uploadedAttachments],
    );

    const handleImageClick = useCallback((zDocId: string, images: AttachmentsForMessage) => {
      const indexOfImageSelected = images.findIndex((img) => img.zDocId === zDocId);
      setImageAttachments(images);
      setSelectedImageIndex(indexOfImageSelected);
      setShouldShowImagesViewer(true);
    }, []);

    const handleCloseImagesViewer = () => setShouldShowImagesViewer(false);

    const handlePersonalContactWarningGoBack = () => {
      setIsPersonalContactWarningModalOpen(false);
    };

    const handlePersonalContactWarningSendAnyway = () => {
      setIsPersonalContactWarningModalOpen(false);
      emitSendAnywayInPersonalInfoWarningModalEvent();
      sendMessage(true);
    };

    return (
      <>
        {shouldShowAttachmentsUploader && (
          <AttachmentsUploader
            existingUploadedAttachments={uploadedAttachments}
            onClose={() => setShouldShowAttachmentsUploader(false)}
            setAttachmentsUploadedForAddMessage={setUploadedAttachments}
          />
        )}

        {shouldShowImagesViewer && (
          <ImagesViewer
            imageAttachments={imageAttachments}
            onClose={handleCloseImagesViewer}
            selectedImageIndex={selectedImageIndex}
          />
        )}

        <TooltipWrapper wrapInTooltip={isAddMessageDisabled} tooltipText={tooltipText}>
          <AddMessageLayout
            ref={ref}
            isSendDisabled={isSendButtonDisabled}
            onSend={sendMessage}
            leftButtons={
              <AddAttachments
                isDisabled={isAddMessageDisabled}
                setIsAttachmentsUploaderOpen={setShouldShowAttachmentsUploader}
              />
            }
            sendMessageDataEventId={SEND_MESSAGE_EVENT_TYPE_ID}
          >
            <TextareaAutoSize
              ariaLabel="Message input;"
              isDisabled={isAddMessageDisabled}
              onBlur={handleBlur}
              onChange={handleTextAreaChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={1}
              shouldFocus={shouldFocusAddMessage}
              value={messageInputText}
            />

            {!!uploadedAttachments.length && (
              <UploadedAttachmentList
                uploadedAttachments={uploadedAttachments}
                onRemoveAttachment={handleRemoveUploadedAttachment}
                onImageClick={handleImageClick}
              />
            )}
          </AddMessageLayout>
        </TooltipWrapper>

        <PersonalContactDetailsWarningModal
          isOpen={isPersonalContactWarningModalOpen}
          warningData={resolveWarningData(activeWarning, personalContactWarningData)}
          onGoBackClick={handlePersonalContactWarningGoBack}
          onSendAnywayClick={handlePersonalContactWarningSendAnyway}
          fallbackLinkUrl={StringConstants.WARNING_MESSAGE.url}
        />
      </>
    );
  },
);

AddMessage.displayName = 'AddMessage';
export default AddMessage;

// Resolve the active warning type to its render-time `warningData` so the
// modal stays reactive to changes in `personalContactWarningData` after it
// opens (e.g. when `useUserWarnings()` resolves between the user clicking
// Send and the user closing the modal).
const resolveWarningData = (
  activeWarning: SendWarningType | null,
  personalContactWarningData: PersonalContactDetailsWarningData | undefined,
): PersonalContactDetailsWarningData | undefined => {
  if (activeWarning === 'ssn') {
    return SSN_WARNING_DATA;
  }
  if (activeWarning === 'personal-contact') {
    return personalContactWarningData;
  }
  return undefined;
};

const getAttachmentDataForAddMessageV2Mutation = (uploadedAttachments: AttachmentsUploadedForAddMessage) => {
  const zDocIds: Array<string> = [];
  const attachmentsForOptimisticMessage = uploadedAttachments.map((uploadedAttachment) => {
    const { file, generatedPreviewUrl, zDocId } = uploadedAttachment;
    const attachmentAdded = new Date().getTime();

    if (zDocId) {
      zDocIds.push(zDocId);
    }

    return {
      attachmentMetadataStatus: 'complete',
      downloadUrl: '',
      fileName: file.name,
      fileSizeBytes: file.size,
      fileType: file.type,
      isAttachmentDeleted: false,
      isAttachmentExpired: false,
      previewUrl: generatedPreviewUrl,
      zDocId,
    };
  });

  return {
    attachmentsForOptimisticMessage,
    zDocIds,
  };
};
